import React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { Link } from '@inertiajs/react'

export interface SessionSidebarItem {
  id: number
  session_id: string
  model: string | null
  started_at: string
  message_count: number
  status: string
  agent: { id: number; username: string; slug: string }
  task: { id: number; summary: string; board_id: number; web_url?: string } | null
}

interface Props {
  sessions: SessionSidebarItem[]
  selectedId: number | null
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function StatusIndicator({ status }: { status: string }) {
  if (status === 'streaming') {
    return (
      <Chip
        icon={
          <FiberManualRecordIcon
            sx={{
              fontSize: 8,
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 },
              },
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        }
        label="Live"
        size="small"
        color="success"
        variant="outlined"
        sx={{ height: 20, fontSize: '0.65rem' }}
      />
    )
  }
  if (status === 'failed') {
    return <Chip label="Failed" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
  }
  if (status === 'aborted') {
    return <Chip label="Aborted" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
  }
  if (status === 'expired') {
    return <Chip label="Expired" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
  }
  return (
    <Typography variant="caption" color="text.disabled" fontSize="0.65rem">
      Done
    </Typography>
  )
}

export function SessionSidebar({ sessions, selectedId }: Props) {
  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Sessions
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {sessions.length}
        </Typography>
      </Box>
      <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
        {sessions.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No session logs found.
            </Typography>
          </Box>
        ) : (
          sessions.map((session) => (
            <ListItemButton
              key={session.id}
              component={Link}
              href={`/monitoring/session_logs/${session.id}`}
              preserveScroll
              selected={session.id === selectedId}
              sx={{
                px: 2,
                py: 1.25,
                minHeight: 44,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              <ListItemText
                disableTypography
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        @{session.agent.username}
                      </Typography>
                      <StatusIndicator status={session.status} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                      {formatRelativeTime(session.started_at)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                      {session.model && (
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                          {session.model}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled" fontSize="0.7rem">
                        {session.message_count} entries
                      </Typography>
                    </Box>
                    {session.task && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {session.task.summary}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          ))
        )}
      </List>
    </Box>
  )
}
